import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/DashboardLayout";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

function ProfileSection({ user, refreshUser }) {
  const [name, setName] = useState(user?.name || "");
  const [language, setLanguage] = useState(user?.preferred_language || "en");
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/content/languages`).then(r => setLanguages(r.data)).catch(() => {});
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, { name, preferred_language: language });
      setSuccess("Profile updated successfully");
      refreshUser();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="profile-section">
      <h2 className="text-lg font-bold mb-4">Profile Information</h2>
      {success && <div className="bg-green-50 border border-green-500 text-green-700 p-3 mb-4 text-sm rounded-lg" data-testid="profile-success">{success}</div>}
      {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg" data-testid="profile-error">{error}</div>}
      <form onSubmit={handleSave} className="space-y-4 max-w-md">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required data-testid="profile-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Email</label>
          <input type="email" value={user?.email || ""} disabled className="w-full border border-gray-200 bg-gray-50 p-3 rounded-lg text-gray-500 cursor-not-allowed" data-testid="profile-email" />
          <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Default Language</label>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="profile-language">
            {languages.length > 0 ? languages.map((l) => (
              <option key={l.code} value={l.code}>{l.name}</option>
            )) : <option value="en">English</option>}
          </select>
          <p className="text-xs text-gray-400 mt-1">AI will generate content in this language by default</p>
        </div>
        <button type="submit" disabled={loading} className="bg-[#0066FF] text-white px-6 py-2.5 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg" data-testid="profile-save">
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </form>
    </div>
  );
}

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      setSuccess("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="password-section">
      <h2 className="text-lg font-bold mb-4">Change Password</h2>
      {success && <div className="bg-green-50 border border-green-500 text-green-700 p-3 mb-4 text-sm rounded-lg" data-testid="password-success">{success}</div>}
      {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg" data-testid="password-error">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Current Password</label>
          <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required data-testid="current-password" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">New Password</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required minLength={6} data-testid="new-password" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Confirm New Password</label>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required minLength={6} data-testid="confirm-password" />
        </div>
        <button type="submit" disabled={loading} className="bg-[#0066FF] text-white px-6 py-2.5 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg" data-testid="password-save">
          {loading ? "Changing..." : "Change Password"}
        </button>
      </form>
    </div>
  );
}

function BrandVoiceSection({ user, refreshUser }) {
  const existingVoice = user?.brand_voice || {};
  const [brandName, setBrandName] = useState(existingVoice.name || "");
  const [tone, setTone] = useState(existingVoice.tone || "");
  const [style, setStyle] = useState(existingVoice.style || "");
  const [description, setDescription] = useState(existingVoice.description || "");
  const [keywords, setKeywords] = useState(existingVoice.keywords || "");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const brandVoice = {
        name: brandName.trim(),
        tone: tone.trim(),
        style: style.trim(),
        description: description.trim(),
        keywords: keywords.trim()
      };
      await axios.put(`${API}/auth/profile`, { brand_voice: brandVoice });
      setSuccess("Brand voice settings saved. Your AI content will now reflect these preferences.");
      refreshUser();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, { brand_voice: {} });
      setBrandName("");
      setTone("");
      setStyle("");
      setDescription("");
      setKeywords("");
      setSuccess("Brand voice settings cleared");
      refreshUser();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="brand-voice-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold">Brand Voice</h2>
          <p className="text-sm text-gray-500 mt-1">Configure your brand personality to influence all AI-generated content</p>
        </div>
        {(brandName || tone || style) && (
          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full" data-testid="brand-voice-active">Active</span>
        )}
      </div>
      {success && <div className="bg-green-50 border border-green-500 text-green-700 p-3 mb-4 text-sm rounded-lg" data-testid="brand-success">{success}</div>}
      {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm rounded-lg" data-testid="brand-error">{error}</div>}
      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Brand Name</label>
            <input type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="e.g., QAIRO, Nike, Mailchimp" className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="brand-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Tone</label>
            <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="brand-tone">
              <option value="">Select a tone...</option>
              <option value="professional">Professional & Authoritative</option>
              <option value="casual">Casual & Friendly</option>
              <option value="playful">Playful & Fun</option>
              <option value="bold">Bold & Direct</option>
              <option value="empathetic">Empathetic & Warm</option>
              <option value="luxurious">Luxurious & Refined</option>
              <option value="technical">Technical & Precise</option>
              <option value="inspirational">Inspirational & Motivating</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Writing Style</label>
          <select value={style} onChange={(e) => setStyle(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="brand-style">
            <option value="">Select a style...</option>
            <option value="concise">Concise & Punchy (short sentences, impactful)</option>
            <option value="storytelling">Storytelling (narrative-driven, engaging)</option>
            <option value="data-driven">Data-Driven (facts, stats, evidence-based)</option>
            <option value="conversational">Conversational (like talking to a friend)</option>
            <option value="academic">Academic (well-researched, formal)</option>
            <option value="journalistic">Journalistic (objective, clear, factual)</option>
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Brand Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Briefly describe your brand, what you do, and who your audience is..." rows={3} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none resize-none rounded-lg" data-testid="brand-description" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Key Terms / Keywords</label>
          <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="Comma-separated terms to naturally include, e.g., AI-powered, scalable, innovative" className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" data-testid="brand-keywords" />
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={loading} className="bg-[#0066FF] text-white px-6 py-2.5 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg" data-testid="brand-save">
            {loading ? "Saving..." : "Save Brand Voice"}
          </button>
          {(brandName || tone || style || description || keywords) && (
            <button type="button" onClick={handleClear} disabled={loading} className="border border-gray-300 px-6 py-2.5 font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 rounded-lg text-gray-600" data-testid="brand-clear">
              Clear
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function SubscriptionSection({ user }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6" data-testid="subscription-section">
      <h2 className="text-lg font-bold mb-4">Subscription & Credits</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-gray-200 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-1">Current Plan</p>
          <p className="text-2xl font-bold capitalize">{user?.subscription || "Free"}</p>
        </div>
        <div className="border border-gray-200 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-1">Credits Remaining</p>
          <p className="text-2xl font-bold text-[#0066FF]">{user?.credits || 0}</p>
        </div>
        <div className="border border-gray-200 p-4 rounded-lg">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-1">Member Since</p>
          <p className="text-2xl font-bold">{user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "N/A"}</p>
        </div>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-gray-600">Manage your account and brand preferences</p>
        </div>

        <div className="space-y-6 max-w-4xl">
          <SubscriptionSection user={user} />
          <ProfileSection user={user} refreshUser={refreshUser} />
          <BrandVoiceSection user={user} refreshUser={refreshUser} />
          <ChangePasswordSection />
        </div>
      </div>
    </DashboardLayout>
  );
}
